import { queryOptions } from '@tanstack/react-query'
import type { components } from '@/api/schema'
import { API_BASE_URL } from '@/api/client'
import type { QuoteBuilderApi } from '../api/client'
import type { Quote } from '../api/types'

// Publishing (USA-1028): the demo's customer-api.ts on the staff routes, with generated types.

type Schemas = components['schemas']

export type PublicationMode = Schemas['QbPublicationCreate']['mode']
export type VisibleSection = Schemas['QbPublicationCreate']['visible_sections'][number]
export type PublicationResult = Schemas['QbPublicationResult']
export type PublicationListItem = Schemas['QbPublicationListItem']
export type QuoteSignature = Schemas['QbQuoteSignatureRead']
export type QuoteDelivery = Schemas['QbQuoteDeliveryRead']
export type QuoteComment = Schemas['QbQuoteCommentRead']
export type QuoteActivity = Schemas['QbActivityRead']
export type QuoteVersion = Schemas['QbVersionRead']

/** Activity a customer caused or saw, shown on Customer Share (demo `share-activity.tsx`). */
export const CUSTOMER_ACTIVITY = [
  'quote_published',
  'quote_sent',
  'share_revoked',
  'invoice_sent',
  'customer_comment_added',
  'customer_decision_recorded',
  'customer_opened',
] as const

export const publicationsQueryOptions = (api: QuoteBuilderApi, quoteId: string) =>
  queryOptions({
    queryKey: ['quote-publications', quoteId],
    queryFn: ({ signal }) => api.request<PublicationListItem[]>(`/quotes/${quoteId}/publications`, { signal }),
  })

export const quoteCommentsQueryOptions = (api: QuoteBuilderApi, quoteId: string) =>
  queryOptions({
    queryKey: ['quote-comments', quoteId],
    queryFn: ({ signal }) => api.request<QuoteComment[]>(`/quotes/${quoteId}/comments`, { signal }),
  })

export const quoteActivityQueryOptions = (api: QuoteBuilderApi, quoteId: string) =>
  queryOptions({
    queryKey: ['quote-activity', quoteId],
    queryFn: ({ signal }) => api.request<QuoteActivity[]>(`/quotes/${quoteId}/activity`, { signal }),
  })

export type QuoteFollowUp = Schemas['QbQuoteFollowUpRead']

export const followUpQueryKey = (quoteId: string) => ['quote-follow-up', quoteId] as const

export const followUpQueryOptions = (api: QuoteBuilderApi, quoteId: string) =>
  queryOptions({
    queryKey: followUpQueryKey(quoteId),
    queryFn: ({ signal }) => api.request<QuoteFollowUp>(`/quotes/${quoteId}/follow-up`, { signal }),
  })

export const updateFollowUp = (
  api: QuoteBuilderApi,
  quoteId: string,
  change: { reminders_paused?: boolean; lost?: boolean },
) => api.request<QuoteFollowUp>(`/quotes/${quoteId}/follow-up`, { method: 'PUT', body: JSON.stringify(change) })

export const quoteVersionsQueryOptions = (api: QuoteBuilderApi, quoteId: string, revision: number | undefined) =>
  queryOptions({
    queryKey: ['quote-versions', quoteId, revision],
    queryFn: ({ signal }) => api.request<QuoteVersion[]>(`/quotes/${quoteId}/versions`, { signal }),
    enabled: revision !== undefined,
  })

export const publishingApi = {
  publish: (
    api: QuoteBuilderApi,
    quote: Pick<Quote, 'id' | 'revision'>,
    mode: PublicationMode,
    visibleSections: VisibleSection[],
    expiresInHours: number,
    requestId: string,
  ) =>
    api.request<PublicationResult>(`/quotes/${quote.id}/publications`, {
      method: 'POST',
      body: JSON.stringify({
        expected_revision: quote.revision,
        mode,
        visible_sections: visibleSections,
        expires_in_hours: expiresInHours,
        request_id: requestId,
      }),
    }),
  send: (api: QuoteBuilderApi, quote: Pick<Quote, 'id' | 'revision'>, publicationNumber: number, requestId: string) =>
    api.request<QuoteDelivery>(`/quotes/${quote.id}/send`, {
      method: 'POST',
      body: JSON.stringify({
        expected_revision: quote.revision,
        publication_number: publicationNumber,
        request_id: requestId,
      }),
    }),
  revoke: (api: QuoteBuilderApi, quoteId: string) =>
    api.request<{ revoked: number }>(`/quotes/${quoteId}/share/revoke`, { method: 'POST' }),
  restore: (api: QuoteBuilderApi, quote: Pick<Quote, 'id' | 'revision'>, version: number) =>
    api.request<Quote>(`/quotes/${quote.id}/versions/${version}/restore`, {
      method: 'POST',
      body: JSON.stringify({ expected_revision: quote.revision }),
    }),
}

/** The customer PDF for a publication, served by the public API route. */
export function documentUrl(path: string) {
  return `${API_BASE_URL}${path}`
}

/** A UUID for retry-safe publish and send; `randomUUID` exists only on HTTPS pages. */
export function newRequestId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
